/**
 * Loads pdf.js on demand (shared by the e-invoice QR reader and the chat document preview).
 * The library and its worker load only the first time a PDF is read.
 */
export type PdfJs = typeof import('pdfjs-dist');

/**
 * pdf.js 6 calls `Promise.try` and `Promise.withResolvers`. Inside the app the global Promise is zone.js's,
 * which lacks them, so the reader stopped with "Promise.try is not a function" on live while it worked
 * stand-alone (2 Nov 2026). Added only when missing, with the standard behaviour.
 */
function ensurePromiseHelpers(): void {
  const P = Promise as any;
  if (typeof P.try !== 'function')
    P.try = (fn: (...args: unknown[]) => unknown, ...args: unknown[]) => new Promise((resolve) => resolve(fn(...args)));
  if (typeof P.withResolvers !== 'function')
    P.withResolvers = () => {
      let resolve!: (value: unknown) => void;
      let reject!: (reason?: unknown) => void;
      const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });
      return { promise, resolve, reject };
    };
}

let pdfjsReady: Promise<PdfJs> | null = null;
export function loadPdfJs(): Promise<PdfJs> {
  ensurePromiseHelpers();
  if (!pdfjsReady)
    pdfjsReady = (async () => {
      const [pdfjs, worker] = await Promise.all([
        import('pdfjs-dist'),
        import('pdfjs-dist/build/pdf.worker.min.mjs'),
      ]);
      // pdf.js runs its worker code in the page from this module, so no worker file has to be served.
      (globalThis as any).pdfjsWorker = worker;
      return pdfjs;
    })().catch(e => {
      pdfjsReady = null;
      throw e;
    });
  return pdfjsReady;
}
